import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {virtualTableRowCheckboxWrapperWidth} from '../../components/common/table/helpers/tableHelpers';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsNotificationsUrl,
  SettingsResourcePagesEnum,
  SettingsTabsPagesEnum,
} from '../../models/url/settingsUrlModel';
import {dismissSettingsInfoBoxAction} from '../../store/data/localPreferences/localPreferencesActions';
import {SettingsContentHeaderWithoutSearch} from '../common/settingsContentHeader';
import {StyledSettingsContent, StyledSettingsMain, StyledSettingsSidebar} from '../common/settingsWrapper';
import {SettingsAdvancedNotificationsTable} from './settingsAdvancedNotificationsTable';
import {SettingsNotificationsSidebar} from './settingsNotificationsSidebar';

/*
 * Props.
 */

interface SettingsAdvancedNotificationsProps {
  toggleSidebar: Handler;
  infoBoxId: string;
  isSidebarOpen: boolean;
  url: SettingsNotificationsUrl;
}

/*
 * Style.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  padding-left: ${virtualTableRowCheckboxWrapperWidth}px;
`;

/*
 * Component.
 */

export const SettingsAdvancedNotificationsList: FC<SettingsAdvancedNotificationsProps> = (props) => {
  const {isSidebarOpen, toggleSidebar, infoBoxId} = props;
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();
  const onInfoBoxCloseClick = () => {
    toggleSidebar();
    dispatch(dismissSettingsInfoBoxAction(infoBoxId));
  };
  const onInfoBoxFooterClick = () => {
    openInBrowser('https://help.front.com/t/6329qd/how-to-change-my-notification-settings');
  };

  return (
    <StyledSettingsMain>
      <StyledSettingsContent>
        <SettingsContentHeaderWithoutSearch
          renderButtons={(buttonProps) => (
            <Button
              {...buttonProps}
              variant={ButtonVariantsEnum.PRIMARY}
              onClick={() => {
                navigateToLink(
                  buildSettingsPath({
                    ...props.url,
                    resourcePage: SettingsResourcePagesEnum.NEW,
                  }),
                );
              }}
            >
              <Trans id="settings-notification-addrule">Add a notification rule</Trans>
            </Button>
          )}
        />
        <StyledTableContainerDiv>
          <SettingsAdvancedNotificationsTable url={props.url} />
        </StyledTableContainerDiv>
      </StyledSettingsContent>
      {isSidebarOpen && (
        <StyledSettingsSidebar>
          <SettingsNotificationsSidebar
            isAdvancedView={props.url.tabPage === SettingsTabsPagesEnum.ADVANCED}
            onCloseClick={onInfoBoxCloseClick}
            onFooterClick={onInfoBoxFooterClick}
          />
        </StyledSettingsSidebar>
      )}
    </StyledSettingsMain>
  );
};
